// Copyright 2019 Sorama B.V.

import { ObjectExtensions } from "extensions/ObjectExtensions";
import { IWorkflowPropertyParser } from "../Interfaces/IWorkflowPropertyParser";
import { WorkflowProperty } from "./WorkflowPropertyObserverManager";

export class WorkflowPropertyBase {
    public name: string;
    public value: unknown;

    /**
     * Used to separate properties. E.g. standard workflow & sidebar
     */
    public prefix?: string;
}

export class WorkflowUriProperty extends WorkflowPropertyBase {
    public scopeIdxs?: number[];
}

export class WorkflowUriModule {
    public name: string;
    public position?: number;
    public id?: number;
    public variant?: string;
}

/**
 * Converts URL to workflow specific objects and the other way around
 */
export class WorkflowPropertyUriConverter {
    public static buildUri(
        workflowName: string,
        modules: WorkflowUriModule[],
        properties: WorkflowProperty[],
        parsers?: { [propertyName: string]: IWorkflowPropertyParser<unknown> }
    ): URL {
        let queryString = WorkflowPropertyUriConverter.WorkflowPropertiesToQuery(properties, parsers).toString();
        const pathString = WorkflowPropertyUriConverter.workflowModulesToUri(modules).toString();
        queryString = queryString.length != 0 ? "?" + queryString : queryString;

        return new URL(workflowName + pathString + queryString, window.location.origin);
    }

    public static workflowNameFromUri(url: URL): string {
        const paths = url.pathname.split("/").filter((x) => x);

        if (paths.length > 0) return paths[0];

        return undefined;
    }

    /**
     *
     * @param path part of the path, only containing modules (and not the workflow name)
     */
    private static workflowModulesFromUriFragment(path: string): WorkflowUriModule[] {
        const result: WorkflowUriModule[] = [];
        const paths = path.split("/").filter((x) => x);

        for (let i = 0; i < paths.length; i++) {
            const regexResult = /(\((-?\d+)\))?(\w*)(\[(\w+)\])?/.exec(paths[i]);

            result.push({
                id: regexResult[2] ? parseInt(regexResult[2]) : undefined,
                name: regexResult[3] ? regexResult[3] : undefined,
                position: i,
                variant: regexResult[5] ? regexResult[5] : undefined,
            });
        }

        return result;
    }

    public static workflowModulesFromUri(url: URL): WorkflowUriModule[] {
        const paths = url.pathname.split("/").filter((x) => x);
        if (paths.length <= 1) return [];

        return WorkflowPropertyUriConverter.workflowModulesFromUriFragment(paths.slice(1).join("/"));
    }

    public static workflowModulesToUri(modules: WorkflowUriModule[]): string {
        const sortedModules = modules.sort((x) => x.position);
        let url = "";

        for (const module of sortedModules) {
            url += "/";

            if (typeof module.id === "number") url += "(" + module.id + ")";

            url += module.name;

            if (module.variant) {
                url += "[" + module.variant + "]";
            }
        }

        return url;
    }

    public static urlToWorkflowProperties(
        params: URLSearchParams,
        parsers?: { [propertyName: string]: IWorkflowPropertyParser<unknown> }
    ): WorkflowUriProperty[] {
        const result: WorkflowUriProperty[] = [];

        const scopeIdx = 4;
        const nameIdx = 7;
        const complexIdx = 9;

        for (const param of params) {
            const key = param[0];
            const value = param[1];

            let parser: (obj: string) => unknown = (val) => {
                return val;
            };

            // Extract property name
            const propertyNameMatch = /(\[(\w+)\])?(\((-?\d+(,(-?\d+)*)*)\))?(\w+)(\[(\w+)\])?/gi.exec(key);

            if (!propertyNameMatch || !propertyNameMatch[nameIdx]) continue; // Invalid property format, so skip this one

            const property = <WorkflowUriProperty>{
                name: propertyNameMatch[nameIdx],
                value: undefined,
                scopeIdxs: undefined,
            };

            parser = parsers && parsers[property.name] ? parsers[property.name].fromWorkflowProperty : parser;
            property.value = parser(value);
            property.prefix = this.getPrefix(key);

            // Extract property scope. eg '(1,2,3)' => [1,2,3]
            if (propertyNameMatch[scopeIdx]) {
                property.scopeIdxs = propertyNameMatch[scopeIdx].split(",").map((x) => parseInt(x));
            }

            if (propertyNameMatch[complexIdx]) {
                // Complex type
                const idx = result.findIndex(
                    (x) => x.name == property.name && ObjectExtensions.deepEqual(x.scopeIdxs, property.scopeIdxs)
                );
                if (idx >= 0) {
                    result[idx].value[propertyNameMatch[complexIdx]] = property.value;
                    continue;
                }

                const newVal = {};
                newVal[propertyNameMatch[complexIdx]] = property.value;
                property.value = newVal;
            }

            result.push(property);
        }

        // Check if some property values are arrays. If so, convert to an "array type"
        for (const property of result) {
            const props = Object.getOwnPropertyNames(property.value);
            const indices = props.filter((x) => !isNaN(Number(x)));

            // All properties in the value are numbers, so it is an array
            if (indices.length > 0 && indices.length == props.length) {
                const newArray = [];
                // We have an array
                for (const idx of indices) {
                    newArray.push(property.value[idx]);
                }
                property.value = newArray;
            }
        }

        return result;
    }

    public static WorkflowPropertiesToQuery(
        workflowProperties: WorkflowProperty[],
        parsers?: { [propertyName: string]: IWorkflowPropertyParser<unknown> }
    ): URLSearchParams {
        const result = new URLSearchParams();

        const parserNames = parsers ? Object.getOwnPropertyNames(parsers) : [];
        for (const workflowProperty of workflowProperties) {
            const propertyName = workflowProperty.name;
            let value = workflowProperty.value;
            const prefix = workflowProperty.prefix ? "[" + workflowProperty.prefix + "]" : "";

            if (value === undefined) {
                // Do nothing with undefined value properties.
                continue;
            }

            const parserName = parserNames.find(
                (x) => workflowProperty.objectName && x.toLowerCase() == workflowProperty.objectName.toLowerCase()
            );

            const parser: (obj: unknown) => string = parserName ? parsers[parserName].toWorkflowProperty : undefined;

            let paramIndexString = "";

            if (workflowProperty.scopeIdxs) {
                paramIndexString += "(" + workflowProperty.scopeIdxs.join() + ")";
            }

            if (Array.isArray(value) && parser) {
                const parsedVal = [];
                for (const val of value) {
                    parsedVal.push(parser(val));
                }
                value = parsedVal;
            } else if (parser) {
                value = parser(value);
            }

            if (typeof value === "object") {
                // Complex object type. Example: ?[prefix](1)foo[bar]=aap&(1)foo[baz]=banaan
                for (const valueKey in value) {
                    if (Object.prototype.hasOwnProperty.call(value, valueKey)) {
                        if (value[valueKey] === undefined || value[valueKey] === null) {
                            continue;
                        }

                        result.append(
                            prefix + paramIndexString + propertyName + "[" + valueKey.toString() + "]",
                            value[valueKey].toString()
                        );
                    }
                }
            } else {
                // Scalar type.Example: ?[prefix](1)foo=bar
                result.append(prefix + paramIndexString + propertyName, value.toString());
            }
        }

        return result;
    }

    public static getPrefix(key: string): string {
        const prefixIdx = 2;
        const propertyNameMatch = /(\[(\w+)\])?(\((-?\d+(,(-?\d+)*)*)\))?(\w+)(\[(\w+)\])?/gi.exec(key);

        const result = propertyNameMatch[prefixIdx];
        return result ? result : ""; // When prefix is undefined, return ampty string
    }
}
